import { Fragment } from 'react';
import clsx from 'clsx';
import { Paragraph, Heading, Text } from '@hungryroot/ramen';
import { type SectionListData } from '../SectionList/utils';
import styles from '../SectionList/SectionList.module.css';

// Ids and anchor links generated by getSectionId function in '../../components/SectionList/utils
export const CaliforniaEmployeeNoticeSections: SectionListData = {
  introduction: {
    title: 'Introduction and Defined Terms',
    body: (
      <Fragment key="introduction">
        <Paragraph className={styles['margin-bottom']}>
          Hungryroot, Inc. (“Hungryroot,” “we,” “us,” or “our”) must collect
          Personal Information from you in order to conduct business and comply
          with federal, state, and local government regulations governing such
          matters as employment, tax, insurance, etc.
        </Paragraph>
        <Paragraph className={styles['margin-bottom']}>
          This notice should be read in conjunction with Hungryroot's general{' '}
          <a href="/privacy" className={styles.link} target="_blank">
            Privacy Policy
          </a>
          , which provides further details on Hungryroot's privacy practices and
          your privacy rights.
        </Paragraph>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            "Employee" -
          </Heading>
          <Text className={styles['inline-text']}>
            This policy applies to the Personal Information of California
            residents who are (i) employees, (ii) job applicants, (iii)
            contractors, interns, and other individuals who perform work for
            Hungryroot (collectively "Non-Employees"), and (iv) employees' and
            Non-Employees' dependents, emergency contacts, and beneficiaries
            (all collectively "HR Individuals"). For convenience, all HR
            Individuals are referred to as "Employees" herein.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            "Personal Information"
          </Heading>
          <Text className={styles['inline-text']}>
            is any information that we can reasonably use to identify you,
            including information that identifies, relates to, describes, is
            reasonably capable of being associated with, or could be reasonably
            linked, directly or indirectly, with a particular individual or
            household. If you reside in California, it includes all personal
            information as defined in the California Consumer Privacy Act as
            amended by the California Privacy Rights Act (collectively, the
            "CCPA"). Personal Information includes "Sensitive Personal
            Information."
          </Text>
        </div>
        <div className={styles.section}>
          <div className={styles['margin-bottom']}>
            <Heading variant="H6" className={styles.inline}>
              "Sensitive Personal Information"
            </Heading>
            <Text className={clsx(styles['inline-text'])}>
              includes special categories of Personal Information, including but
              not limited to, a social security number, a driver's license
              number, a passport number, an account login, a password, a precise
              geolocation, racial or ethnic origin, political opinions,
              religious or philosophical beliefs, or trade union membership,
              genetic data, biometric data, data concerning health, and data
              concerning a natural person's sex life or sexual orientation.
              Sensitive Personal Information may also include specific dietary
              preferences or dietary restriction requests, as shared by you.
            </Text>
          </div>
          <Paragraph className={styles['margin-bottom']}>
            The nature of the information collected varies somewhat for each
            Employee, depending on the applicable role and employment
            responsibilities, your citizenship, the location of the facility
            where you work, and other factors. We collect Personal Information
            from you solely for business purposes, including those related
            directly or indirectly to your application for employment and/or
            employment with Hungryroot, and those required by governmental
            agencies.
          </Paragraph>
          <Paragraph className={styles['margin-bottom']}>
            Data collected may include, without limitation, such things as:
          </Paragraph>

          <ul
            className={clsx(
              styles['list-indented-bullet'],
              styles['two-column']
            )}
          >
            <li>
              <Text className={styles.inline}>Name</Text>
            </li>
            <li>
              <Text className={styles.inline}>Mailing address(es)</Text>
            </li>

            <li>
              <Text className={styles.inline}>User ID(s)</Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Banking and other financial data
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>Phone number(s)</Text>
            </li>
            <li>
              <Text className={styles.inline}>Email address(es)</Text>
            </li>

            <li>
              <Text className={styles.inline}>
                Government identification numbers, e.g., Social Security number,
                driver's license number
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>Date of birth</Text>
            </li>
            <li>
              <Text className={styles.inline}>Gender, race, and ethnicity</Text>
            </li>
            <li>
              <Text className={styles.inline}>Health and disability data</Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Family-related data, e.g., marital status, family member
                information
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Personal and health-related data for you and your family
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>Trade union data</Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Payroll and benefits information
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>Emergency contacts</Text>
            </li>
            <li>
              <Text className={styles.inline}>Work permit status</Text>
            </li>
            <li>
              <Text className={styles.inline}>Military status</Text>
            </li>
            <li>
              <Text className={styles.inline}>Nationality</Text>
            </li>
            <li>
              <Text className={styles.inline}>Residency</Text>
            </li>
            <li>
              <Text className={styles.inline}>Passport information</Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Retirement account information
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Physical limitations and special needs in order to provide
                reasonable accommodations
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Date of hire, date(s) of promotion(s), work history, technical
                skills, educational background, professional certifications and
                registrations, language capabilities, and training records
              </Text>
            </li>
            <li>
              <Text className={styles.inline}>
                Records of work absences, vacation/paid time off, and sick pay
              </Text>
            </li>
          </ul>
        </div>
      </Fragment>
    ),
  },
  collection: {
    title: 'How Data is Collected',
    body: (
      <Fragment key="collection">
        <Paragraph className={styles['margin-bottom']}>
          Generally, we collect Personal Information directly from you in
          circumstances where you provide such data (during the onboarding
          process, for example). However, in some instances, the Personal
          Information we collect has been provided by other means including your
          interactions with us and by third parties (e.g., employer
          organizations, recruiting companies, insurance companies, your spouse
          or dependent, public records, automated technologies, government
          agencies and acquired companies).
        </Paragraph>
      </Fragment>
    ),
  },
  use: {
    title: 'Use of the Personal Information We Collect',
    body: (
      <Fragment key="use">
        <Paragraph className={styles['margin-bottom']}>
          The primary purposes for collection, storage and/or use of your
          Personal Information include, but are not limited to:
        </Paragraph>
        <ul className={styles['list-indented-bullet']}>
          <li>
            <Heading variant="H6" className={styles.inline}>
              Human Resources Management.
            </Heading>{' '}
            <Text className={clsx(styles['inline-text'])}>
              We collect, store, analyze, and share (internally) Personal
              Information in order to attract, retain and motivate a highly
              qualified workforce. This includes recruiting, compensation
              planning, succession planning, reorganization needs, performance
              assessment, training, employee benefit administration, compliance
              with applicable legal requirements, and communication with
              Employees and/or their representatives.
            </Text>
          </li>
          <li>
            <Heading variant="H6" className={styles.inline}>
              Business Processes and Management.
            </Heading>{' '}
            <Text className={clsx(styles['inline-text'])}>
              Personal Information is used to run our business operations
              including, for example, scheduling work assignments, managing
              company assets, and populating employee directories. Information
              may also be used to comply with government regulations.
            </Text>
          </li>
          <li>
            <Heading variant="H6" className={styles.inline}>
              Communication and Identification.
            </Heading>{' '}
            <Text className={clsx(styles['inline-text'])}>
              We use your Personal Information to identify you and to
              communicate with you.
            </Text>
          </li>
        </ul>
      </Fragment>
    ),
  },
  disclosure: {
    title: 'Disclosure of Personal Information',
    body: (
      <Fragment key="disclosure">
        <Paragraph className={styles['margin-bottom']}>
          Hungryroot acts to protect your Personal Information and ensure that
          unauthorized individuals do not have access to your Information by
          using reasonable security measures to protect Personal Information. We
          will not knowingly disclose, sell or otherwise distribute your
          Personal Information to any third party without your knowledge and,
          where appropriate, your express written permission,{' '}
          <span className={styles.italic}>except</span> under the following
          circumstances.
        </Paragraph>
        <ul className={styles['list-indented-bullet']}>
          <li>
            <Heading variant="H6" className={styles.inline}>
              Legal requests and investigations.
            </Heading>{' '}
            <Text className={clsx(styles['inline-text'])}>
              We may disclose your Personal Information when such disclosure is
              reasonably necessary (i) to prevent fraud; (ii) to comply with any
              applicable statute, law, rule or regulation; or (iii) to comply
              with a court order.
            </Text>
          </li>
          <li>
            <Heading variant="H6" className={styles.inline}>
              Third-party vendors and service providers.
            </Heading>{' '}
            <Text className={clsx(styles['inline-text'])}>
              We may, from time to time, outsource services, functions, or
              operations of our business to third-party service providers, such
              as payroll and benefits service providers. When engaging in such
              outsourcing, it may be necessary for us to disclose your Personal
              Information to those service providers. In some cases, the service
              providers may collect Personal Information directly from you on
              our behalf.
            </Text>
          </li>
          <li>
            <Heading variant="H6" className={styles.inline}>
              Business Transfers:
            </Heading>{' '}
            <Text className={clsx(styles['inline-text'])}>
              During the term of your employment, we may buy other companies,
              create new subsidiaries or business units or sell part or all of
              Hungryroot or its assets. Although we do not anticipate
              transferring any of your Personal Information to such entities, in
              the event that we do, your Personal Information will remain
              subject to the protections outlined in the then current Privacy
              Policy.
            </Text>
          </li>
          <li>
            <Heading variant="H6" className={styles.inline}>
              Protection of Company and Other.
            </Heading>{' '}
            <Text className={clsx(styles['inline-text'])}>
              We may release Personal Information when we believe release is
              necessary to comply with the law; enforce or apply our policies
              and other agreements; or protect the rights, property, or safety
              of Hungryroot, our employees, or others. This disclosure will
              never, however, include selling, renting, sharing or otherwise
              disclosing your Personal Information for commercial purposes in
              violation of the commitments set forth in this Privacy Policy.
            </Text>
          </li>
        </ul>
        <Paragraph className={styles['margin-bottom']}>
          Hungryroot does not sell nor disclose Employees' Personal Information
          for cross-context behavioral advertising.
        </Paragraph>
      </Fragment>
    ),
  },
  rights: {
    title: 'Your Rights Under the CCPA/CPRA',
    body: (
      <Fragment key="rights">
        <Paragraph className={styles['margin-bottom']}>
          As of the date that this policy was published, eligible California
          Employees have the following rights under the California Consumer
          Privacy Act as amended by the California Privacy Rights Act
          (collectively, the "CCPA"):
        </Paragraph>
        <Heading
          variant="H6"
          className={clsx(styles.underline, styles['margin-bottom'])}
        >
          Right to Know
        </Heading>
        <Paragraph className={styles['margin-bottom']}>
          You have a right to submit a verifiable request for copies of your
          Personal Information that Hungryroot has held over the past 12 months,
          free of charge, including:
        </Paragraph>
        <ul
          className={clsx(
            styles['list-indented-bullet'],
            styles['margin-bottom']
          )}
        >
          <li>
            <Text className={styles.inline}>
              The categories of personal information that we have collected.
            </Text>
          </li>
          <li>
            <Text className={styles.inline}>
              The categories of sources from which we collected personal
              information.
            </Text>
          </li>
          <li>
            <Text className={styles.inline}>
              The business or commercial purpose for collecting, selling, or
              sharing personal information.
            </Text>
          </li>
          <li>
            <Text className={styles.inline}>
              The categories of third parties to whom we disclose personal
              information.
            </Text>
          </li>
          <li>
            <Text className={styles.inline}>
              The specific pieces of personal information we have collected.
            </Text>
          </li>
        </ul>
        <div className={styles['margin-bottom']}>
          <Heading variant="H6" className={styles.underline}>
            Right to deletion
          </Heading>
          <Text>
            At your request, we will delete your Personal Information unless we
            need to keep such information for legal purposes and/or to provide
            services or further the relationship with us that you have requested
            (including, but not limited to your role as an employee or other
            employment or business relationship).
          </Text>
        </div>
        <div className={styles['margin-bottom']}>
          <Heading variant="H6" className={styles.underline}>
            Right to opt out of sale or sharing.
          </Heading>
          <Text>
            Subject to certain business and legal exceptions, you may request
            that we stop selling or sharing your Personal Information for
            cross-context behavioral advertising. As noted above, Hungryroot
            does not sell nor disclose Employees' Personal Information for
            cross-context behavioral advertising.
          </Text>
        </div>
        <div className={styles['margin-bottom']}>
          <Heading variant="H6" className={styles.underline}>
            Right to correct.
          </Heading>
          <Text>
            We will use commercially reasonable efforts to comply with your
            request to correct any inaccurate Personal Information.
          </Text>
        </div>
        <div className={styles['margin-bottom']}>
          <Heading variant="H6" className={styles.underline}>
            Right to limit the use and disclosure of Sensitive Personal
            Information
          </Heading>
          <Text>
            Subject to certain business and legal exceptions, you may request
            that we limit the use and disclosure of Sensitive Personal
            Information.
          </Text>
        </div>
        <div className={styles['margin-bottom']}>
          <Heading variant="H6" className={styles.underline}>
            Right to data portability
          </Heading>
          <Text>
            At your request, and subject to certain exceptions, we will provide
            you free of charge with any of your Personal Information that we
            hold, and will provide it in a structured, commonly used and
            machine-readable format.
          </Text>
        </div>
        <div className={styles['margin-bottom']}>
          <Heading variant="H6" className={styles.underline}>
            Right to nondiscrimination
          </Heading>
          <Text>
            You are entitled to exercise the rights described above free from
            discriminatory treatment prohibited by the CCPA.
          </Text>
        </div>
      </Fragment>
    ),
  },
  retention: {
    title: 'Document Retention',
    body: (
      <Fragment key="retention">
        <Paragraph className={styles['margin-bottom']}>
          We keep your Personal Information for as long as needed to fulfill the
          particular purpose for which it was collected and in accordance with
          our record retention schedule. We may also retain your records if
          legally required or to fulfill a legitimate business interest.
        </Paragraph>
      </Fragment>
    ),
  },
  updates: {
    title: "Updates to Hungryroot's CCPA/CPRA Employee Privacy Policy",
    body: (
      <Fragment key="updates">
        <Paragraph className={styles['margin-bottom']}>
          Hungryroot reserves the right to modify this CCPA/CPRA Employee
          Privacy Policy at any time. Your provision of Personal Information to
          use after any change in this policy will constitute your acceptance of
          such change.
        </Paragraph>
      </Fragment>
    ),
  },
  exercise: {
    title: 'How to Exercise Your Rights',
    body: (
      <Fragment key="exercise">
        <Paragraph className={styles['margin-bottom']}>
          Hungryroot will respond to permitted requests in accordance with
          applicable laws if it can verify the identity of the individual
          submitting the request. You can exercise your rights under the CCPA by
          contacting Hungryroot at{' '}
          <a href="mailto:help@hungryroot.com" className={styles.link}>
            help@hungryroot.com
          </a>
          .
        </Paragraph>
        <Paragraph className={styles['margin-bottom']}>
          You may also refer to Hungryroot's general{' '}
          <a href="/privacy" className={styles.link} target="_blank">
            Privacy Policy
          </a>{' '}
          for additional information.
        </Paragraph>
      </Fragment>
    ),
  },
};
